import app from "@/main";
import {BorcluTur, IKurumDVO} from "../uyap/CommonInterfaces";
import {SnapshotBorclu, SnapshotPaketi} from "./types";
import {normalizerBul, fallbackHash} from "./normalizers";
import {kanonikHash} from "./kanonik";
import {dosyaKimligiCoz, dosyaEsasNoNormalize, temizDize} from "./DosyaRefHafizasi";

/**
 * Faz 4 İP2 — EKRAN kaynaklı canlı sorgunun tekil snapshot yazımı.
 *
 * NEDEN: bugün depoya yalnız GECE KOŞUMU yazıyor (`SnapshotYazici`, koşum sonu
 * toplu POST). Gündüz elle koşan bir sorgu depoya hiç düşmediği için (a) diğer
 * uygulama onu göremiyor, (b) `FrekansBekcisi` o alanı "vadeli" sayıp aynı gece
 * BOŞUNA yeniden koşuyor. Bu sınıf çift ücretin ikinci yönünü kapatır.
 *
 * SINIR: görev/koşum akışına DOKUNULMAZ. `SnapshotYazici` ve
 * `TakipSorgulaTaskHandler` aynen kalır; buradaki yazım `UyapRequest.run()`
 * içindeki CANLI cevap yolundan, tek paketlik gövdeyle atılır (yazma ucu tek
 * elemanlı diziyi zaten kabul ediyor).
 *
 * FIRE-AND-FORGET: ekran akışını bloklamaz, hatası sorgu sonucunu düşürmez —
 * `SnapshotYazici` ile aynı sözleşme (ASLA throw etmez).
 *
 * BAĞLAM: `dosya_ref` cevabın kendi `dosyaOzetDVO`sundan da kurulabilirdi, ama
 * `DosyaRefHafizasi` üzerinden okunur; orada `DosyaBul`dan öğrenilmiş `takipId`
 * de bulunur ve pakete taşınırsa sunucu `borclu_ref`i gece koşumuyla AYNI
 * referansa çözer. Hafızada yoksa cevabın kendi özetine düşülür.
 */
export class TekilSnapshotYazici {
    static async yaz(alan: string, cevap: any): Promise<void> {
        try {
            if (!alan || !cevap || typeof cevap !== "object") {
                return;
            }

            const paket = TekilSnapshotYazici.paketKur(alan, cevap);
            if (!paket) {
                return;
            }

            await app.$http.post("/api/v1/sorgu-snapshot", [paket]);
        } catch (e) {
            console.warn(`TekilSnapshotYazici: '${alan}' snapshot'ı yazılamadı.`, e);
        }
    }

    private static paketKur(alan: string, cevap: any): SnapshotPaketi | null {
        const ozet = cevap?.dosyaOzetDVO;
        const dosyaId = ozet?.dosyaId;

        // kod inceleme: dosyaKimligiCoz zaten null/undefined'ı kendi içinde
        // temizDize ile "" yapıp haritada bulamayınca null döner — burada
        // ayrıca korumaya gerek yok (T2-33).
        const hafiza = dosyaKimligiCoz(dosyaId);

        const birimId = hafiza ? hafiza.birimId : temizDize(ozet?.birimDVO?.birimId);
        const dosyaEsasNo = dosyaEsasNoNormalize(hafiza ? hafiza.dosyaEsasNo : temizDize(ozet?.dosyaNo));

        // Kararlı dosya kimliği kurulamıyorsa paket kurulamaz (Faz 1 ilkesi):
        // dosyaId volatil olduğu için anahtar ASLA ona dayanmaz.
        if (birimId === "" || dosyaEsasNo === "") {
            return null;
        }

        const borclu = TekilSnapshotYazici.borcluPaketi(cevap?.borcluBilgileri);
        if (!borclu) {
            return null;
        }

        let entities: SnapshotPaketi["entities"] = [];
        let paketOzeti: SnapshotPaketi["ozet"];
        let hash: string;

        try {
            const normalizer = normalizerBul(alan);
            if (normalizer) {
                const sonuc = normalizer(cevap);
                entities = sonuc.entities || [];
                paketOzeti = sonuc.ozet;
                hash = kanonikHash(entities);
            } else {
                // Normalizer'ı olmayan alan da yazılır: `FrekansBekcisi` "bugün
                // koştu"yu (fetched_at/last_seen_at) alandan bağımsız okuyor,
                // gece gereksiz koşumu bu satır engelliyor.
                entities = [];
                hash = fallbackHash(cevap);
            }
        } catch (e) {
            console.warn(`TekilSnapshotYazici: '${alan}' normalize edilemedi, fallback'e düşüldü.`, e);
            entities = [];
            hash = fallbackHash(cevap);
        }

        return {
            birimId,
            dosyaEsasNo,
            takipId: hafiza ? hafiza.takipId : null,
            // K4: parse edilmiş ham cevap olduğu gibi taşınır (IndexedDB eşleniği).
            govde: cevap,
            alan,
            borclu,
            // Gece koşumundan ayırt edilebilsin diye 'manuel'; yazma ucu bu değeri
            // zaten kabul ediyor (SorguSnapshotYazRequest: in:gorev,manuel).
            kaynak: "manuel",
            fetchedAt: new Date().toISOString(),
            hash,
            entities,
            ozet: paketOzeti,
        };
    }

    /**
     * Cevabın `borcluBilgileri` zarfından `SnapshotBorclu` kurar.
     *
     * `SnapshotYazici.borcluPaketi` ile aynı kurallar: kurum VKN'si maskesiz
     * geldiği için taşınır, kişi TCKN'si maskeli geldiği için TAŞINMAZ (kalıcı
     * referansı sunucu çözer). Kimliksiz borçlu paketi kurulmaz — sunucu
     * doğrulaması da onu reddederdi.
     */
    private static borcluPaketi(borcluBilgileri: any): SnapshotBorclu | null {
        const kisiKurumId = Number(borcluBilgileri?.kisiKurumId);
        if (!Number.isFinite(kisiKurumId) || kisiKurumId <= 0) {
            return null;
        }

        const paket: SnapshotBorclu = {
            kisiKurumId,
            turu: borcluBilgileri.turu,
        };

        if (borcluBilgileri.turu === BorcluTur.KURUM && borcluBilgileri.kurumDVO) {
            paket.vergiNo = (borcluBilgileri.kurumDVO as IKurumDVO).vergiNo;
            const kurumAdi = (borcluBilgileri.kurumDVO as IKurumDVO).kurumAdi;
            if (kurumAdi) paket.adSoyad = kurumAdi;
        } else if (borcluBilgileri.kisiTumDVO) {
            const ad = [borcluBilgileri.kisiTumDVO.adi, borcluBilgileri.kisiTumDVO.soyadi]
                .filter((p: any) => typeof p === "string" && p.length > 0)
                .join(" ")
                .trim();
            if (ad) paket.adSoyad = ad;
        }

        return paket;
    }
}
